import { useEffect, useState } from 'react';

/**
 * Campaign tracking hook for capturing and storing UTM parameters
 *
 * @typedef {Object} TrackingEntry
 * @property {string} referrer - Traffic source
 * @property {string} campaign - Campaign name
 * @property {string} medium - Campaign medium
 * @property {string} cid - Click ID (gclid, fbclid, etc)
 * @property {string} ecid - External campaign ID
 * @property {string} ad - Ad content/creative
 * @property {string} adset - Ad set/group
 * @property {number} timestamp - Entry timestamp
 *
 * @typedef {Object} CampaignData
 * @property {string} [reserved_campaign_source_first]
 * @property {string} [reserved_campaign_id_first]
 * @property {string} [reserved_campaign_medium_first]
 * @property {string} [reserved_campaign_cid_first]
 * @property {string} [reserved_campaign_ecid_first]
 * @property {string} [reserved_ad_first]
 * @property {string} [reserved_adset_first]
 * @property {string} [reserved_campaign_source_last]
 * @property {string} [reserved_campaign_id_last]
 * @property {string} [reserved_campaign_cid_last]
 * @property {string} [reserved_campaign_ecid_last]
 * @property {string} [reserved_campaign_medium_last]
 * @property {string} [reserved_ad_last]
 * @property {string} [reserved_adset_last]
 * @property {string} [reserved_campaign_source_calculated]
 * @property {string} [reserved_campaign_id_calculated]
 * @property {string} [reserved_campaign_medium_calculated]
 * @property {string} [reserved_campaign_cid_calculated]
 * @property {string} [reserved_campaign_ecid_calculated]
 * @property {string} [reserved_ad_calculated]
 * @property {string} [reserved_adset_calculated]
 *
 * @typedef {Object} UseCampaignTrackerReturn
 * @property {CampaignData} campaignData - Current campaign data
 * @property {Function} trackCampaign - Function to manually track campaign
 * @property {Function} clearTracking - Function to clear all tracking data
 */

const COOKIE_NAME = 'SingitaSourceTracking';
const COOKIE_EXPIRY_DAYS = 30;

const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = typeof document !== 'undefined' ? document.cookie.split(';') : [];
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const setCookie = (name, value, days) => {
  if (typeof document === 'undefined') return;

  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie =
    name + '=' + (value || '') + expires + '; path=/; SameSite=Lax';
};

const deleteCookie = (name) => {
  if (typeof document !== 'undefined') {
    document.cookie =
      name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }
};

const parseUTMParams = () => {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmCampaign = params.get('utm_campaign');
  const utmMedium = params.get('utm_medium');
  const utmContent = params.get('utm_content');
  const utmTerm = params.get('utm_term');
  const fbclid = params.get('fbclid');
  const gclid = params.get('gclid');
  const msclkid = params.get('msclkid');

  // Determine referrer source
  let referrer = utmSource || 'direct';
  let cid = '';
  let ecid = '';

  if (fbclid) {
    referrer = 'facebook';
    cid = fbclid;
  } else if (gclid) {
    referrer = 'google';
    cid = gclid;
  } else if (msclkid) {
    referrer = 'bing';
    cid = msclkid;
  } else if (
    !utmSource &&
    typeof document !== 'undefined' &&
    document.referrer
  ) {
    // Parse referrer domain
    try {
      const referrerUrl = new URL(document.referrer);
      const domain = referrerUrl.hostname.toLowerCase();

      if (domain.includes('facebook.com') || domain.includes('fb.com')) {
        referrer = 'facebook';
      } else if (domain.includes('google.')) {
        referrer = 'google';
      } else if (domain.includes('bing.com')) {
        referrer = 'bing';
      } else if (domain.includes('linkedin.com')) {
        referrer = 'linkedin';
      } else if (domain.includes('twitter.com') || domain.includes('t.co')) {
        referrer = 'twitter';
      } else {
        referrer = domain;
      }
    } catch (e) {
      // Invalid referrer URL
    }
  }

  // Get entry path and additional tracking data
  const entryPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const entryUrl = typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';
  
  return {
    referrer,
    campaign: utmCampaign || '',
    medium: utmMedium || '',
    cid: cid || '',
    ecid: ecid || '',
    ad: utmContent || '',
    adset: utmTerm || '',
    entryPath,
    entryUrl,
    timestamp: Date.now(),
  };
};

/**
 * Hook for tracking campaign UTM parameters
 * @returns {UseCampaignTrackerReturn} Campaign tracking utilities
 */
export const useCampaignTracker = () => {
  const [campaignData, setCampaignData] = useState({});
  const [isClient, setIsClient] = useState(false);

  const trackCampaign = () => {
    if (typeof window === 'undefined') return;

    const utmData = parseUTMParams();
    if (!utmData) return;

    // Get existing tracking data
    const cookie = getCookie(COOKIE_NAME);
    let tracking = [];

    //console.log('Campaign Tracker - Tracking campaign:', utmData);

    if (cookie) {
      try {
        const decoded =
          typeof window !== 'undefined' && window.atob
            ? window.atob(cookie)
            : cookie;
        tracking = JSON.parse(decoded);
        if (!Array.isArray(tracking)) {
          tracking = [];
        }
      } catch (err) {
        tracking = [];
      }
    }

    // Add new tracking entry if it has meaningful data
    if (utmData.referrer !== 'direct' || utmData.campaign || utmData.medium) {
      tracking.push(utmData);

      // Limit to last 10 entries to prevent cookie size issues
      if (tracking.length > 10) {
        tracking = tracking.slice(-10);
      }

      // Save updated tracking
      const encoded =
        typeof window !== 'undefined' && window.btoa
          ? window.btoa(JSON.stringify(tracking))
          : JSON.stringify(tracking);
      setCookie(COOKIE_NAME, encoded, COOKIE_EXPIRY_DAYS);
    }
  };

  const clearTracking = () => {
    deleteCookie(COOKIE_NAME);
    setCampaignData({});
  };

  // First effect: Set client flag after hydration
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Second effect: Track and read campaign data only on client
  useEffect(() => {
    if (!isClient) return;

    // Track current page on mount
    trackCampaign();

    // Read and parse existing tracking data
    const cookie = getCookie(COOKIE_NAME);
    if (cookie) {
      try {
        const decoded =
          typeof window !== 'undefined' && window.atob
            ? window.atob(cookie)
            : cookie;
        const tracking = JSON.parse(decoded);

        if (Array.isArray(tracking) && tracking.length > 0) {
          // Calculate the most relevant source
          let referrer = tracking[tracking.length - 1].referrer;
          let campaign = tracking[tracking.length - 1].campaign;
          let medium = tracking[tracking.length - 1].medium;
          let cid = tracking[tracking.length - 1].cid;
          let ecid = tracking[tracking.length - 1].ecid;
          let ad = tracking[tracking.length - 1].ad;
          let adset = tracking[tracking.length - 1].adset;

          // Prioritize paid sources (facebook > google > bing > others)
          for (const entry of tracking) {
            if (
              entry.referrer === 'facebook' ||
              entry.referrer === 'google' ||
              entry.referrer === 'bing'
            ) {
              referrer = entry.referrer;
              campaign = entry.campaign;
              medium = entry.medium;
              cid = entry.cid;
              ecid = entry.ecid;
              ad = entry.ad;
              adset = entry.adset;

              // Facebook has highest priority
              if (entry.referrer === 'facebook') break;
            }
          }

          setCampaignData({
            reserved_campaign_source_first: tracking[0].referrer,
            reserved_campaign_id_first: tracking[0].campaign,
            reserved_campaign_medium_first: tracking[0].medium,
            reserved_campaign_cid_first: tracking[0].cid,
            reserved_campaign_ecid_first: tracking[0].ecid,
            reserved_ad_first: tracking[0].ad,
            reserved_adset_first: tracking[0].adset,
            reserved_entry_path_first: tracking[0].entryPath,
            reserved_entry_url_first: tracking[0].entryUrl,
            reserved_campaign_source_last:
              tracking[tracking.length - 1].referrer,
            reserved_campaign_id_last: tracking[tracking.length - 1].campaign,
            reserved_campaign_cid_last: tracking[tracking.length - 1].cid,
            reserved_campaign_ecid_last: tracking[tracking.length - 1].ecid,
            reserved_campaign_medium_last: tracking[tracking.length - 1].medium,
            reserved_ad_last: tracking[tracking.length - 1].ad,
            reserved_adset_last: tracking[tracking.length - 1].adset,
            reserved_entry_path_last: tracking[tracking.length - 1].entryPath,
            reserved_entry_url_last: tracking[tracking.length - 1].entryUrl,
            reserved_campaign_source_calculated: referrer,
            reserved_campaign_id_calculated: campaign,
            reserved_campaign_medium_calculated: medium,
            reserved_campaign_cid_calculated: cid,
            reserved_campaign_ecid_calculated: ecid,
            reserved_ad_calculated: ad,
            reserved_adset_calculated: adset,
            reserved_session_count: tracking.length,
            reserved_first_seen: new Date(tracking[0].timestamp).toISOString(),
            reserved_last_seen: new Date(tracking[tracking.length - 1].timestamp).toISOString(),
          });
        }
      } catch (err) {
        // Fallback for non-array format
        try {
          const tracking = JSON.parse(cookie);
          if (tracking && typeof tracking === 'object') {
            setCampaignData(tracking);
          }
        } catch (e) {
          // Invalid cookie format
        }
      }
    }
  }, [isClient]);

  return {
    campaignData,
    trackCampaign,
    clearTracking,
  };
};
